/**
 * <stay-ai-bundle-remove>
 *
 * Removes an ENTIRE Stay.AI bundle (parent + every child line sharing the same
 * _bundle_id) from the cart in one request. The line keys are rendered into
 * data-stay-bundle-keys server-side (Liquid has every cart line key), so we
 * avoid a /cart.js round-trip (abnormally slow on this store after a bundle add).
 *
 * Re-render follows the native theme's cart event contract (mirrors the ATC fix
 * in lsg-bundle.js — see CXE-3973):
 *   - drawer surface: dispatch cart:change (baseEvent "line-item:change") so the
 *     native <cart-drawer> re-renders from cart.sections[sectionId]; cart:refresh
 *     reconciles the floating <cart-count>.
 *   - cart page: re-render the main-cart section in place from the response
 *     sections (no full reload), then cart:refresh for the count bubble. The
 *     cart-page section id is passed via data-stay-cart-section-id (absent on the
 *     drawer, where the drawer registers itself via cart:prepare-bundled-sections).
 *
 * Batch-zero via /cart/update.js mirrors stay-ai-cart.js deleteOrphanBundleProducts().
 */
class StayAiBundleRemove extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-stay-bundle-remove]') || this;
    this.clickHandler = this.onClick.bind(this);
    this.button.addEventListener('click', this.clickHandler);
  }

  disconnectedCallback() {
    if (this.button && this.clickHandler) {
      this.button.removeEventListener('click', this.clickHandler);
    }
  }

  async onClick(event) {
    event.preventDefault();

    const keys = (this.dataset.stayBundleKeys || '')
      .split(',')
      .map((key) => key.trim())
      .filter(Boolean);

    if (keys.length === 0) return;

    const updates = {};
    keys.forEach((key) => {
      updates[key] = 0;
    });

    // Ask the theme's <cart-drawer> (and any other listeners) which section ids
    // need re-rendering, so the /cart/update.js response carries their fresh HTML.
    const sectionsSet = new Set();
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsSet },
      })
    );

    // On the cart page, the main-cart section isn't a bundled-section listener,
    // so include it explicitly to re-render it in place (no full reload).
    const cartSectionId = this.dataset.stayCartSectionId;
    if (cartSectionId) sectionsSet.add(cartSectionId);

    const body = { updates };
    if (sectionsSet.size > 0) {
      body.sections = [...sectionsSet];
      body.sections_url = window.location.pathname;
    }

    this.button.setAttribute('aria-busy', 'true');
    document.documentElement.dispatchEvent(
      new CustomEvent('theme:loading:start', { bubbles: true })
    );

    try {
      const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', { bubbles: true })
        );
        return;
      }

      const cart = await response.json();

      // Cart page: swap the main-cart section's HTML in place.
      if (cartSectionId && cart.sections && cart.sections[cartSectionId] != null) {
        const sectionElement = document.getElementById(`shopify-section-${cartSectionId}`);
        if (sectionElement) {
          const parsed = new DOMParser().parseFromString(
            cart.sections[cartSectionId],
            'text/html'
          );
          const fresh = parsed.getElementById(`shopify-section-${cartSectionId}`);
          if (fresh) sectionElement.innerHTML = fresh.innerHTML;
        }
      }

      // Drawer + count bubble: native theme contract.
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'line-item:change', cart },
        })
      );
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:refresh', { bubbles: true })
      );
    } catch (error) {
      console.error('Stay bundle remove failed:', error);
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:refresh', { bubbles: true })
      );
    } finally {
      this.button.removeAttribute('aria-busy');
      document.documentElement.dispatchEvent(
        new CustomEvent('theme:loading:end', { bubbles: true })
      );
    }
  }
}

if (!window.customElements.get('stay-ai-bundle-remove')) {
  window.customElements.define('stay-ai-bundle-remove', StayAiBundleRemove);
}
